import {computed, Injectable, signal} from '@angular/core';
import {Observable, ReplaySubject} from 'rxjs';
import {
  QueueAssignmentList,
  QueueType,
  UserModel,
  UserTypes
} from 'src/app/types/api/dot-net-api-types/data-contracts';

@Injectable({
  providedIn: 'root'
})
export class UserDataService {
  private sessionUser$ = new ReplaySubject<UserModel | null>(1);

  public get sessionUser(): Observable<UserModel | null> {
    return this.sessionUser$.asObservable();
  }

  public set sessionUser(user: UserModel | null) {
    this.sessionUser$.next(user);
  }

  public completeSessionUser() {
    this.sessionUser$.complete();
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Signal Based Architecture
  // -------------------------------------------------------------------------------------------------------------------
  /**
   * The current user logged in
   * @private
   */
  private _currentUser = signal<UserModel | null>(null);

  /**
   * The current logged-in user's queue assignments
   * @private
   */
  private _currentUserQueueAssignments = signal<QueueAssignmentList[]>([]);

  /**
   * Flag that trags if user is loading
   * @private
   */
  private _isLoading = signal<boolean>(true);

  /**
   * Flag that tracks if user initialization has started or not.
   * @private
   */
  private _hasInitialized = signal<boolean>(false);

  /**
   * Read-only signal for the currently logged-in user
   */
  currentUser = this._currentUser.asReadonly();

  /**
   * Read-only signal for the currently logged-in user's queue assignments
   */
  currentUserQueueAssignments = this._currentUserQueueAssignments.asReadonly();

  /**
   * Read-only signal that tracks if the user is still loading
   */
  isLoading = this._isLoading.asReadonly();

  /**
   * Read-only signal that tracks if user initialization has started.
   */
  hasInitialized = this._hasInitialized.asReadonly();

  /**
   * Computed read-only signal that is true or false, depending on if the user is null or not.
   */
  isAuthenticated = computed<boolean>(() => this._currentUser() !== null);

  /**
   * Computed read-only signal that holds the current logged-in user's userType.
   */
  userType = computed<UserTypes | null>(() => this.currentUser()?.userTypeCode ?? null);

  /**
   * Computed signal that tracks if loading and initialization has finished.
   */
  hasFinishedLoading = computed(() => !this._isLoading() && this._hasInitialized());

  /**
   * Called when user data has started loading.
   * @param loading
   */
  setLoading(loading: boolean): void {
    this._isLoading.set(loading);
  }

  /**
   * Called when auth initialization is complete
   */
  setInitialized(): void {
    this._hasInitialized.set(true);
    this._isLoading.set(false);
  }

  /**
   * Computed signal that determines if the current user is an admin
   */
  readonly isAdmin = computed<boolean>(() =>
    this.userType() === UserTypes.CourtAdmin ||
    this.userType() === UserTypes.ClerkAdmin ||
    this.userType() === UserTypes.CVManager  ||
    this.userType() === UserTypes.FSXSupport  
  );

  /**
   * Computed signal that determines if the current user is within the ag security group
   */
  readonly isAG = computed<boolean>(() =>
    this.userType() === UserTypes.AGAdminCV ||
    this.userType() === UserTypes.AGAdminCC ||
    this.userType() === UserTypes.AttorneyGeneralCC ||
    this.userType() === UserTypes.AttorneyGeneralCV ||
    this.userType() === UserTypes.AGStaffCC ||
    this.userType() === UserTypes.AGStaffCV
  );

  /**
   * Computed signal that checks if the current user is a specific CV AG
   */
  readonly isAG_CV = computed<boolean>(() =>
    this.userType() === UserTypes.AGAdminCV ||
    this.userType() === UserTypes.AttorneyGeneralCV ||
    this.userType() === UserTypes.AGStaffCV
  );

  /**
   * Computed signal that checks if the current user is a CV AG Admin
   */
  readonly isAG_CVAdmin = computed<boolean>(() =>
    this.userType() === UserTypes.AGAdminCV    
  );

  /**
   * Computed signal that checks if the current user is a CV AG Admin
   */
  readonly isAG_CCAdmin = computed<boolean>(() =>
    this.userType() === UserTypes.AGAdminCC    
  );

  /**
   * Computed signal that checks if the current user is a specific CV AG
   */
  readonly isAG_CC = computed<boolean>(() =>
    this.userType() === UserTypes.AGAdminCC ||
    this.userType() === UserTypes.AttorneyGeneralCC ||
    this.userType() === UserTypes.AGStaffCC
  );

  /**
   * Computed signal that determines if the current user is an attorney.
   */
  readonly isAttorney = computed<boolean>(() => this.userType() === UserTypes.Attorney);

  /**
   * Computed signal that determines if the current user is within the court staff security group
   */
  readonly isClerkStaff = computed<boolean>(() => this.userType() === UserTypes.ClerkStaff);

  /**
   * Computed signal that determines if the current user is within the court staff security group
   */
  readonly isClerkGroup = computed<boolean>(() => this.userType() === UserTypes.ClerkStaff || this.userType() === UserTypes.ClerkAdmin);

/**
   * Computed signal that determines if the current user is within the court staff security group
   */
  readonly isJudgeGroup = computed<boolean>(() => this.userType() === UserTypes.JudgeAdmin || this.userType() === UserTypes.Judge);

  /**
   * Computed signal that determines if the current user is within the court staff security group
   */
  readonly isCourtStaff = computed<boolean>(() => this.userType() === UserTypes.CourtStaff);

  /**
   * Computed signal that determines if the current user is within the court staff security group
   */
  readonly isCVStaff = computed<boolean>(() => this.userType() === UserTypes.CVStaff);

  /**
   * Computed signal that determines if the current user is within the external users group
   */
  readonly isExternal = computed<boolean>(() =>
    this.userType() === UserTypes.ProSe ||
    this.userType() === UserTypes.Attorney
  );

  /**
   * Computed signal that determines if the current user is within the judicial security group
   */
  readonly isJudicial = computed<boolean>(() =>
    this.userType() === UserTypes.JudgeAdmin ||
    this.userType() === UserTypes.Judge ||
    this.userType() === UserTypes.Commissioner ||
    this.userType() === UserTypes.JudicialStaff
  );

  /**
   * Computed signal that determines if the current user is self-represented.
   */
  readonly isSelfRepresented = computed<boolean>(() => this.userType() === UserTypes.ProSe);

  /**
   * Computed signal that determines if the current user is within the application support group
   */
  readonly isSupport = computed<boolean>(() =>
    this.userType() === UserTypes.FSXSupport
  );

  /**
   * Computed signal that determines if the current user is within the application support group
   */
  readonly isStateAgency = computed<boolean>(() =>
    this.userType() === UserTypes.StateAgency
  );

  /**
   * Sets the current logged-in user
   * @param user
   */
  setCurrentUser(user: UserModel | null): void {
    this._currentUser.set(user);
  }

  /**
   * Sets the current logged-in user's queue assignments
   * @param assignments
   */
  setUserQueueAssignments(assignments: QueueAssignmentList[]): void {
    this._currentUserQueueAssignments.set(assignments);
  }

  /**
   * A single role check.
   * @param role
   */
  hasRole(role: UserTypes): boolean {
    return this.userType() === role;
  }

  /**
   * Multiple role check
   * @param roles
   */
  hasAnyRole(roles: UserTypes[]): boolean {
    const currUserType = this.userType();
    return currUserType !== null && roles.includes(currUserType);
  }

  /**
   * Checks if any of the queue types passed in are included in any of the user's assignments.
   * @param queueTypes
   */
  hasQueueAssignment(queueTypes: QueueType[]): boolean {
    const currUserQueueAssignmentTypes = this.currentUserQueueAssignments().map(qal => qal.queueType);
    return queueTypes.some(queueType => currUserQueueAssignmentTypes.includes(queueType));
  }
}
